import { useRef, useState } from 'react';
import { useAsyncResource } from '@hooks';
import { getFile } from '@entities';
import { ImageView } from '@components';
import PdfPreview from './PdfPreview';
import styles from './FileEvidence.module.css';

function FileResource({
  companyId,
  siteId,
  assetId,
  folder,
  fileName,
  kind = 'report',
  preview = false,
  refreshKey = 0,
}) {
  const refreshRef = useRef(refreshKey > 0);
  const [imageError, setImageError] = useState(null);
  const [imageReady, setImageReady] = useState(false);

  const canLoad = [
    companyId,
    siteId,
    assetId,
    folder,
    fileName,
  ].every(value => (
    value !== null &&
    value !== undefined &&
    String(value).trim() !== ''
  ));

  const resource = useAsyncResource(
    async () => {
      const forceRefresh = refreshRef.current;
      refreshRef.current = false;

      const result = await getFile(
        companyId,
        siteId,
        assetId,
        folder,
        fileName,
        { forceRefresh }
      );

      const value = typeof result === 'string'
        ? result
        : result?.url;

      if (typeof value !== 'string' || !value.trim()) {
        throw new Error('The file API did not return a URL.');
      }

      const url = new URL(value, window.location.href);

      if (!['http:', 'https:'].includes(url.protocol)) {
        throw new Error('The file API returned an unsupported URL.');
      }

      return url.href;
    },
    [companyId, siteId, assetId, folder, fileName],
    {
      initialData: null,
      enabled: canLoad,
    }
  );

  function retry() {
    refreshRef.current = true;
    setImageError(null);
    setImageReady(false);
    resource.reload();
  }

  if (!canLoad) {
    return (
      <p className={styles.message} role="status">
        The location or filename needed to load this file is missing.
      </p>
    );
  }

  if (resource.isLoading) {
    return (
      <p className={styles.message} role="status">
        {kind === 'image' ? 'Loading image…' : 'Preparing report…'}
      </p>
    );
  }

  const error = resource.error || imageError;

  if (error) {
    return (
      <div className={styles.feedback}>
        <p className={styles.error} role="alert">{error}</p>

        <button
          type="button"
          className={styles.button}
          onClick={retry}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!resource.data) return null;

  if (kind === 'image') {
    return (
      <div className={styles.imageFrame}>
        <ImageView
          imagePath={resource.data}
          alt={fileName}
          containerStyle={{
            backgroundColor: 'var(--color-grey)',
          }}
          onLoad={() => setImageReady(true)}
          onError={() => {
            setImageError('The image could not be loaded.');
          }}
        />

        {!imageReady && (
          <p className={styles.imageLoading} role="status">
            Loading image…
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={preview ? styles.reportPreview : styles.reportLink}>
      <a
        className={styles.button}
        href={resource.data}
        target="_blank"
        rel="noopener noreferrer"
      >
        Open report in a new tab
      </a>

      {preview && <PdfPreview url={resource.data} title={`Report: ${fileName}`} />}
    </div>
  );
}

export default function FileEvidence(props) {
  const identity = JSON.stringify([
    props.companyId,
    props.siteId,
    props.assetId,
    props.folder,
    props.fileName,
    props.kind,
    props.refreshKey,
  ]);

  return <FileResource key={identity} {...props} />;
}